export function isValidCreditCardNumber(number: string): boolean {
  const cleaned = number.replace(/\D/g, "");
  if (cleaned.length < 13 || cleaned.length > 19) return false;
  let sum = 0;
  let shouldDouble = false;
  for (let i = cleaned.length - 1; i >= 0; i--) {
    let digit = parseInt(cleaned.charAt(i), 10);
    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    shouldDouble = !shouldDouble;
  }
  return sum % 10 === 0;
}

export function detectCardBrand(number: string): string | null {
  const cleaned = number.replace(/\D/g, "");
  if (!cleaned) return null;
  if (/^4/.test(cleaned)) return "VISA";
  if (/^5[1-5]/.test(cleaned) || /^2[2-7]/.test(cleaned)) return "MASTERCARD";
  if (/^3[47]/.test(cleaned)) return "AMEX";
  if (/^6(?:011|5)/.test(cleaned)) return "DISCOVER";
  if (/^3(?:0[0-5]|[68])/.test(cleaned)) return "DINERS";
  if (
    /^(?:4011|4312|4389|4514|4573|5041|5066|5067|509|6277|6362|6363|650|6516|6550)/.test(
      cleaned,
    )
  )
    return "ELO";
  if (/^(?:606282|3841)/.test(cleaned)) return "HIPERCARD";
  if (/^35/.test(cleaned)) return "JCB";
  return null;
}

export function ccvLengthForBrand(brand: string | null): number {
  return brand === "AMEX" ? 4 : 3;
}

export function isValidHolderName(name: string): boolean {
  const trimmed = name.trim();
  if (trimmed.length < 2) return false;
  if (!/^[a-zA-ZÀ-ÿ\s]+$/.test(trimmed)) return false;
  return true;
}

/**
 * Validates a 2-digit year expiry (e.g. UI form input "MM"/"YY").
 * Used by the customer-facing subscription form.
 */
export function isValidExpiryDate(month: string, year2digits: string): boolean {
  const m = parseInt(month, 10);
  const y2 = parseInt(year2digits, 10);
  if (isNaN(m) || isNaN(y2)) return false;
  if (m < 1 || m > 12) return false;
  if (year2digits.length !== 2) return false;
  const fullYear = 2000 + y2;
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  if (fullYear < currentYear) return false;
  if (fullYear === currentYear && m < currentMonth) return false;
  if (fullYear > currentYear + 20) return false;
  return true;
}

/**
 * Validates a 4-digit year expiry (e.g. server payload "MM"/"YYYY").
 * Used by the gateway integration.
 */
export function isValidExpiryDate4(month: string, year4digits: string): boolean {
  const m = parseInt(month, 10);
  const y = parseInt(year4digits, 10);
  if (isNaN(m) || isNaN(y)) return false;
  if (m < 1 || m > 12) return false;
  if (year4digits.length !== 4) return false;
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  if (y < currentYear) return false;
  if (y === currentYear && m < currentMonth) return false;
  if (y > currentYear + 20) return false;
  return true;
}

export function formatCardNumberMask(value: string): string {
  const cleaned = value.replace(/\D/g, "").slice(0, 19);
  return cleaned.replace(/(.{4})/g, "$1 ").trim();
}

export function isValidCPF(cpf: string): boolean {
  const cleaned = cpf.replace(/\D/g, "");
  if (cleaned.length !== 11) return false;
  if (/^(\d)\1+$/.test(cleaned)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleaned.charAt(i), 10) * (10 - i);
  }
  let firstDigit = 11 - (sum % 11);
  if (firstDigit >= 10) firstDigit = 0;
  if (firstDigit !== parseInt(cleaned.charAt(9), 10)) return false;
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleaned.charAt(i), 10) * (11 - i);
  }
  let secondDigit = 11 - (sum % 11);
  if (secondDigit >= 10) secondDigit = 0;
  if (secondDigit !== parseInt(cleaned.charAt(10), 10)) return false;
  return true;
}

export function isValidCNPJ(cnpj: string): boolean {
  const cleaned = cnpj.replace(/\D/g, "");
  if (cleaned.length !== 14) return false;
  if (/^(\d)\1+$/.test(cleaned)) return false;
  const checkDigit = (len: number): number => {
    const weights =
      len === 12
        ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
        : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    let sum = 0;
    for (let i = 0; i < len; i++) {
      sum += parseInt(cleaned.charAt(i), 10) * weights[i]!;
    }
    const rest = sum % 11;
    return rest < 2 ? 0 : 11 - rest;
  };
  if (checkDigit(12) !== parseInt(cleaned.charAt(12), 10)) return false;
  if (checkDigit(13) !== parseInt(cleaned.charAt(13), 10)) return false;
  return true;
}

/**
 * CPF (11 dígitos) ou CNPJ (14 dígitos). O campo cpfCnpj da Asaas aceita
 * ambos — customer, cobrança e PIX Automático funcionam igual para PJ.
 */
export function isValidCpfCnpj(doc: string): boolean {
  const cleaned = doc.replace(/\D/g, "");
  if (cleaned.length === 11) return isValidCPF(cleaned);
  if (cleaned.length === 14) return isValidCNPJ(cleaned);
  return false;
}

export interface CardValidationInput {
  holderName: string;
  number: string;
  /** Numeric month, e.g. "1" or "01". Will be padded to 2 digits internally. */
  expiryMonth: string;
  /** 4-digit year, e.g. "2026". */
  expiryYear: string;
  ccv: string;
}

/** Server-side card-payload validator. Returns null if valid, or an error string. */
export function validateCard(input: CardValidationInput): string | null {
  if (!isValidHolderName(input.holderName)) {
    return "Nome inválido. Digite o nome como está no cartão.";
  }
  if (!isValidCreditCardNumber(input.number)) {
    return "Número do cartão inválido";
  }
  if (!isValidExpiryDate4(input.expiryMonth.padStart(2, "0"), input.expiryYear)) {
    return "Data de validade inválida!";
  }
  const brand = detectCardBrand(input.number);
  const expectedCcv = ccvLengthForBrand(brand);
  const ccvDigits = input.ccv.replace(/\D/g, "");
  if (ccvDigits.length !== expectedCcv) {
    return "Código de segurança (CVV) inválido";
  }
  return null;
}
